//src/design-system/Organisms/NotificationSection/notificationAccordionItem.tsx

import { useRef, useEffect, useState } from "react";
import type { FC } from "react";
import { accordionStyles } from "@/design-system/Molecules/AccordionList/Accordion.styles";
import { Button } from "@/design-system/Atoms";
import type { Notification } from "@/rtkQuery/features/models/notification.model";
import {
  useAcknowledgeNotificationMutation,
  useMarkNotificationAsViewedMutation,
} from "@/rtkQuery/features/services/userNotification/userNotificationApi.slice";
import { useNavigate } from "react-router-dom";

interface NotificationAccordionItemProps {
  notification: Notification;
  isOpen: boolean;
  onToggle: () => void;
}

export const NotificationAccordionItem: FC<NotificationAccordionItemProps> = ({
  notification,
  isOpen,
  onToggle,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState("0px");
  const navigate = useNavigate();
  const [acknowledgeNotification] = useAcknowledgeNotificationMutation();
  const [markNotificationAsViewed] = useMarkNotificationAsViewedMutation();

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen]);

  const handleToggle = async () => {
    const needsViewed = notification.UserViewed === false;
    onToggle();
    if (needsViewed) {
      markNotificationAsViewed(notification.Id).catch((err) =>
        console.error("View error:", err)
      );
    }
  };

  const handleContinueClick = async () => {
    const needsAcknowledge = notification.UserAcknowledged === false;
    if (needsAcknowledge) {
      acknowledgeNotification(notification.Id).catch((err) =>
        console.error("Acknowledge error:", err)
      );
    }
    navigate(notification.NotificationLink);
  };

  let formattedMessage = notification.Message;

  if (formattedMessage?.includes("ICP") || formattedMessage?.includes("PDE5")) {
    formattedMessage = formattedMessage
      .replace(/\b(ICP)(?!\s)/g, "$1 ")
      .replace(/\b(PDE5)(?!\s)/g, "$1 ");
  }

  return (
    <div className={`${accordionStyles.item}`}>
      <h2
        className={`${accordionStyles.header(isOpen)} animate-fade-in-up`}
        onClick={handleToggle}
        aria-expanded={isOpen}
        role="button"
        tabIndex={0}
        id={`${notification.Id}`}
        aria-controls={`panel-${notification.Id}`}
        onKeyDown={(e) => e.key === "Enter" && handleToggle()}
      >
        <button
          type="button"
          className={accordionStyles.buttonIcon}
          aria-label="Toggle Accordion"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          <span
            className={`accordion-arrow-icon-wrapper ${isOpen ? "rotate" : ""}`}
          >
            <span className="accordion-arrow-icon" />
          </span>
        </button>
        {notification && notification.Title}
      </h2>

      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{ maxHeight }}
        id={`panel-${notification.Id}`}
        aria-labelledby={`${notification.Id}`}
      >
        <div className={accordionStyles.panel}>
          <div>{formattedMessage}</div>
          <div className="mt-2 lg:mt-4 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2">
            <Button
              variant="graySubmitButton"
              size="xl"
              className={`${
                notification.NotificationLink?.trim()
                  ? ""
                  : "invisible pointer-events-none"
              }`}
              type="button"
              onClick={handleContinueClick}
            >
              Continue now
            </Button>
            {notification?.HasTimeEstimation &&
              Number(notification.TimeEstimation) > 0 && (
                <div className="text-sm text-gray-600">
                  Time estimation: Approximately{" "}
                  {notification.TimeEstimation}{" "}
                  {Number(notification.TimeEstimation) === 1
                    ? "minute"
                    : "minutes"}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};
